import React from "react";
import {
  FileSignature,
  Users,
  Heart,
  HeartHandshake,
  Briefcase,
  DoorOpen,
  Sparkles,
  UserPlus,
  Handshake,
  KeyRound,
  ArrowRight,
  Gift,
  UsersRound,
  Landmark,
  Home,
  Building2,
  TrendingUp,
  PiggyBank,
} from "lucide-react";
import { Button } from "./ui/button";
import { BrandName } from "./BrandName";
import {
  IMAGES,
  agreementTopics,
  whoCanDubbleUpp,
  howItWorks,
  investorSteps,
  investorTypes,
} from "../mock";

const iconMap = { Users, Heart, HeartHandshake, Briefcase, DoorOpen, Sparkles, UserPlus, Handshake, KeyRound, Landmark, Building2, TrendingUp, PiggyBank };

const Badge = ({ icon: Icon, children }) => (
  <span className="inline-flex items-center gap-2 rounded-full border border-[#3CC54F]/40 bg-white px-4 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-[#25862F]">
    {Icon && <Icon className="h-3.5 w-3.5" />}
    {children}
  </span>
);

/* ---------------- Buyer + Buyer ---------------- */
export const BuyerBuyer = ({ onGetStarted }) => (
  <section id="buyer-buyer" className="bg-white py-20 lg:py-28">
    <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 lg:grid-cols-2 lg:px-8">
      <div className="relative order-2 lg:order-1">
        <div className="overflow-hidden rounded-3xl shadow-2xl shadow-slate-900/15">
          <img
            src={IMAGES.roommates}
            alt="Two roommates moving into the home they bought together"
            className="h-[420px] w-full object-cover"
            loading="lazy"
          />
        </div>
      </div>
      <div className="order-1 lg:order-2">
        <Badge icon={UsersRound}>Buyer + Buyer</Badge>
        <h2 className="mt-5 font-heading text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
          Buyer + Buyer.
        </h2>
        <p className="mt-3 font-heading text-lg font-bold text-[#2FA33F] md:text-xl">
          Two Buyers. One Home. One Clear Plan.
        </p>
        <p className="mt-6 text-lg leading-relaxed text-slate-600">
          Combine income, savings, and buying power with someone you know to create a stronger path
          to homeownership.
        </p>
        <p className="mt-6 text-lg leading-relaxed text-slate-600">
          You may be able to qualify for more, split housing costs, and start building equity
          instead of continuing to rent separately.
        </p>
        <Button
          data-testid="buyer-buyer-cta"
          onClick={onGetStarted}
          className="mt-8 rounded-full bg-[#3CC54F] px-7 py-3 text-[15px] font-semibold text-white shadow-md shadow-[#3CC54F]/25 transition-colors duration-200 hover:bg-[#25862F]"
        >
          Start Buying Together <ArrowRight className="ml-1 h-4 w-4" />
        </Button>
      </div>
    </div>
  </section>
);

/* ---------------- Who can DubbleUpp ---------------- */
export const WhoCan = () => (
  <section className="bg-white pb-20 lg:pb-28">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="rounded-3xl bg-[#F7FCF7] px-6 py-14 ring-1 ring-[#3CC54F]/20 sm:px-12">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Who can <BrandName />?
          </h2>
          <p className="mt-4 text-lg text-slate-600">
            You can buy a home with a friend, family, co-workers, a roommate — the possibilities
            with DubbleUpp are endless!
          </p>
        </div>
        <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {whoCanDubbleUpp.map(({ label, icon }) => {
            const Icon = iconMap[icon] || Users;
            return (
              <div
                key={label}
                className="group flex flex-col items-center gap-3 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-100 transition-shadow duration-200 hover:shadow-lg"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#EAF9EA] transition-colors duration-200 group-hover:bg-[#3CC54F]/25">
                  <Icon className="h-6 w-6 text-[#25862F]" />
                </span>
                <p className="text-sm font-bold text-slate-800">{label}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);

/* ---------------- Agreement ---------------- */
export const Agreement = () => (
  <section id="agreement" className="bg-white py-20 lg:py-28">
    <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 lg:grid-cols-2 lg:px-8">
      <div className="relative">
        <div className="overflow-hidden rounded-3xl shadow-2xl shadow-slate-900/15">
          <img
            src={IMAGES.agreement}
            alt="Two co-buyers reviewing their DubbleUpp agreement together"
            className="h-[440px] w-full object-cover"
            loading="lazy"
          />
        </div>
      </div>
      <div>
        <Badge icon={FileSignature}>The DubbleUpp Agreement</Badge>
        <h2 className="mt-5 font-heading text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
          Know the Plan Before You Buy.
        </h2>
        <p className="mt-6 text-lg leading-relaxed text-slate-600">
          Buying a home together is a major commitment. DubbleUpp helps both buyers put the
          important decisions in writing before closing.
        </p>
        <p className="mt-6 text-lg leading-relaxed text-slate-600">
          The agreement can establish how you will handle{" "}
          <span className="font-semibold text-slate-900">
            {agreementTopics.slice(0, -1).join(", ")}, and {agreementTopics.at(-1)}
          </span>
          .
        </p>
        <p className="mt-6 text-lg leading-relaxed text-slate-600">
          If life changes, you already have a plan for what happens next.
        </p>
        <p className="mt-3 text-lg font-semibold text-slate-900">
          Buy together with clarity from day one.
        </p>
      </div>
    </div>
  </section>
);

/* ---------------- Team ---------------- */
export const Team = () => (
  <section className="bg-[#F7FCF7] py-20 lg:py-28">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="mx-auto max-w-3xl text-center">
        <Badge icon={Handshake}>Your Team</Badge>
        <h2 className="mt-5 font-heading text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
          Your Homeownership Team.
        </h2>
        <p className="mt-5 text-lg leading-relaxed text-slate-600">
          DubbleUpp helps both buyers navigate the real estate and mortgage process together. From
          financing and home search to showings, negotiations, and closing, your team helps
          coordinate the entire purchase so both buyers understand what is happening and what comes
          next.
        </p>
      </div>
      <div className="mx-auto mt-12 max-w-4xl overflow-hidden rounded-3xl shadow-xl shadow-slate-900/10">
        <img
          src={IMAGES.team}
          alt="Your DubbleUpp homeownership team: agent, mortgage expert, and showing assistant"
          className="w-full object-cover"
          loading="lazy"
        />
      </div>
    </div>
  </section>
);

/* ---------------- Investor Program ---------------- */
export const Investor = ({ onGetStarted }) => (
  <section id="investor" className="bg-white py-20 lg:py-28">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="grid items-center gap-14 lg:grid-cols-2">
        <div>
          <Badge icon={Landmark}>Investor Program</Badge>
          <h2 className="mt-5 font-heading text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
            <BrandName /> Investor Program.
          </h2>
          <p className="mt-3 font-heading text-lg font-bold text-[#3CABDA] md:text-xl">
            Real Estate Investing, Reinvented. Back a Buyer. Own a Stake.
          </p>
          <p className="mt-5 text-lg leading-relaxed text-slate-600">
            You don't need to buy a whole house to invest in one. With the DubbleUpp Investor
            Program, you contribute toward the purchase of a home someone else lives in — and you
            own a piece of the upside.
          </p>
          <p className="mt-4 text-lg leading-relaxed text-slate-600">
            No tenants to manage. No repairs to chase. No landlord headaches. Just a clear,
            written agreement that spells out how the value gets shared when the home is sold or
            refinanced.
          </p>
          <Button
            data-testid="investor-cta"
            onClick={onGetStarted}
            className="mt-8 rounded-full bg-[#3CC54F] px-7 py-3 text-[15px] font-semibold text-white shadow-md shadow-[#3CC54F]/25 transition-colors duration-200 hover:bg-[#25862F]"
          >
            Become an Investor <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
        <div className="relative">
          <div className="overflow-hidden rounded-3xl shadow-2xl shadow-slate-900/15">
            <img
              src={IMAGES.investor}
              alt="An investor and a homebuyer reviewing their DubbleUpp agreement together"
              className="h-[440px] w-full object-cover"
              loading="lazy"
            />
          </div>
        </div>
      </div>

      {/* Who can invest */}
      <div className="mt-20 rounded-3xl bg-[#F4FAFD] px-6 py-14 ring-1 ring-[#3CABDA]/20 sm:px-12" data-testid="who-can-invest">
        <div className="mx-auto max-w-2xl text-center">
          <h3 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            Who Can Invest?
          </h3>
          <p className="mt-4 text-lg text-slate-600">
            Anyone with capital and a reason to believe in a buyer. You don't need to be a parent —
            you just need to want a stake in real estate.
          </p>
        </div>
        <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {investorTypes.map(({ label, icon }) => {
            const Icon = iconMap[icon] || Landmark;
            return (
              <div
                key={label}
                className="group flex flex-col items-center gap-3 rounded-2xl bg-white p-6 text-center shadow-sm ring-1 ring-slate-100 transition-shadow duration-200 hover:shadow-lg"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#E6F4FA] transition-colors duration-200 group-hover:bg-[#3CABDA]/25">
                  <Icon className="h-6 w-6 text-[#2B8EBB]" />
                </span>
                <p className="text-sm font-bold text-slate-800">{label}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Win-win steps */}
      <div className="mt-20">
        <h3 className="text-center font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
          Here's How a Deal Comes Together.
        </h3>
        <p className="mx-auto mt-4 max-w-2xl text-center text-lg text-slate-600">
          One example — a parent backing their adult child — but the same structure works for any
          investor and any buyer.
        </p>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {investorSteps.map((s) => (
            <div
              key={s.num}
              className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition-shadow duration-200 hover:shadow-xl"
            >
              <span className="font-heading text-4xl font-extrabold text-[#3CC54F]">{s.num}</span>
              <h4 className="mt-3 font-heading text-lg font-bold text-slate-900">{s.title}</h4>
              <p className="mt-2 leading-relaxed text-slate-600">{s.text}</p>
            </div>
          ))}
        </div>
        <p className="mx-auto mt-10 max-w-2xl text-center text-lg font-semibold leading-relaxed text-slate-800">
          The buyer gets into a home sooner.{" "}
          <span className="text-[#2FA33F]">
            The investor gets a stake in real estate — without buying the whole property.
          </span>
        </p>
      </div>
    </div>
  </section>
);

/* ---------------- Dark CTA: More Than a Gift ---------------- */
export const MoreThanGift = ({ onGetStarted }) => (
  <section className="bg-white pb-20 lg:pb-28">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="relative overflow-hidden rounded-[2.5rem] bg-gradient-to-br from-[#0B1F2A] via-[#123244] to-[#1E6B26] px-6 py-16 text-center sm:px-12 lg:py-20">
        <div className="pointer-events-none absolute -top-20 right-10 h-64 w-64 rounded-full bg-[#3CC54F]/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 left-10 h-64 w-64 rounded-full bg-[#3CC54F]/15 blur-3xl" />
        <span className="relative inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-[#8FE39A]">
          <Gift className="h-3.5 w-3.5" />
          Smarter Than a Gift
        </span>
        <h2 className="relative mx-auto mt-6 max-w-3xl font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
          Invest in People. Invest in Property.
        </h2>
        <p className="relative mt-6 text-xl font-semibold text-[#B9EEC1]">
          A gift helps once. An investment keeps working.
        </p>
        <p className="relative mx-auto mt-4 max-w-2xl text-lg leading-relaxed text-white/80">
          Whether you're backing family, a friend, or a buyer you've never met, DubbleUpp turns
          your contribution into a structured stake in the home — with the terms, timeline, and
          payout spelled out from day one.
        </p>
        <p className="relative mt-4 text-lg font-semibold text-white">
          Help someone become a homeowner <span className="text-[#6FD87C]">and grow your money while you do it.</span>
        </p>
        <Button
          data-testid="gift-cta"
          onClick={onGetStarted}
          className="relative mt-9 h-auto rounded-full bg-white px-8 py-4 text-base font-bold text-[#1E6B26] shadow-lg transition-colors duration-200 hover:bg-[#EAF9EA]"
        >
          Explore the Investor Program <ArrowRight className="ml-1 h-5 w-5" />
        </Button>
      </div>
    </div>
  </section>
);

/* ---------------- How It Works ---------------- */
export const HowItWorks = () => (
  <section id="how-it-works" className="bg-[#F7FCF7] py-20 lg:py-28">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="mx-auto max-w-2xl text-center">
        <Badge icon={Sparkles}>Make Big Moves, Together</Badge>
        <h2 className="mt-5 font-heading text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
          Here's how it works...
        </h2>
      </div>
      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {howItWorks.map(({ step, title, text, icon }) => {
          const Icon = iconMap[icon] || Sparkles;
          return (
            <div
              key={step}
              className="group relative rounded-3xl bg-white p-8 shadow-sm ring-1 ring-slate-100 transition-shadow duration-200 hover:shadow-xl"
            >
              <div className="flex items-center justify-between">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[#EAF9EA] transition-colors duration-200 group-hover:bg-[#3CC54F]/25">
                  <Icon className="h-7 w-7 text-[#25862F]" />
                </span>
                <span className="font-heading text-5xl font-extrabold text-slate-100">{step}</span>
              </div>
              <h3 className="mt-6 font-heading text-xl font-bold text-slate-900">{title}</h3>
              <p className="mt-3 leading-relaxed text-slate-600">{text}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ---------------- Two Ways + Final CTA ---------------- */
export const TwoWaysFinal = ({ onGetStarted }) => (
  <section className="bg-white py-20 lg:py-28">
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <div className="mx-auto max-w-2xl text-center">
        <Badge icon={Home}>Two Ways</Badge>
        <h2 className="mt-5 font-heading text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
          One Program. Two Ways to Buy Together.
        </h2>
      </div>
      <div className="mx-auto mt-12 grid max-w-4xl gap-6 md:grid-cols-2">
        <div className="rounded-3xl border-2 border-[#3CC54F]/30 bg-[#F7FCF7] p-9 transition-shadow duration-200 hover:shadow-xl">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#3CC54F]/20">
            <UsersRound className="h-6 w-6 text-[#25862F]" />
          </span>
          <h3 className="mt-5 font-heading text-2xl font-extrabold text-slate-900">Buyer + Buyer</h3>
          <p className="mt-3 leading-relaxed text-slate-600">
            Two people combine their buying power and own a home together.
          </p>
        </div>
        <div className="rounded-3xl border-2 border-[#3CC54F]/30 bg-[#F7FCF7] p-9 transition-shadow duration-200 hover:shadow-xl">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#3CC54F]/20">
            <Landmark className="h-6 w-6 text-[#25862F]" />
          </span>
          <h3 className="mt-5 font-heading text-2xl font-extrabold text-slate-900">Buyer + Investor</h3>
          <p className="mt-3 leading-relaxed text-slate-600">
            One person lives in and pays for the home while another helps fund the purchase and
            participates according to the agreed investment structure.
          </p>
        </div>
      </div>

      <div className="mx-auto mt-16 max-w-3xl text-center">
        <h2 className="font-heading text-3xl font-extrabold leading-snug tracking-tight text-slate-900 sm:text-4xl">
          Don't just buy a home.{" "}
          Buy more of the home you want with <BrandName tm />.
        </h2>
        <Button
          data-testid="final-cta"
          onClick={onGetStarted}
          className="group mt-9 h-auto rounded-full bg-[#3CC54F] px-9 py-4 text-lg font-bold text-white shadow-lg shadow-[#3CC54F]/30 transition-colors duration-200 hover:bg-[#25862F]"
        >
          Get Started With DubbleUpp
          <ArrowRight className="ml-1.5 h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
        </Button>
        <p className="mt-5 text-sm text-slate-500">Sign up today. It's free.</p>
      </div>
    </div>
  </section>
);
